import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch} from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { DesignedConfirmDialog } from 'components';
import {
  getStoreCustomerGroupsApi,
  deleteCustomerAccountApi,
  loginAsCustomerApi,
  resetCustomerPasswordApi,
  getCustomerActiveCreditCardsApi
} from 'api';
import { genderOptions } from 'helpers/customers';
import { convertUTCDateToLocalDate } from 'helpers/date';


const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      fontSize: '13px',
      padding: '12px 24px'
    }
  }
});


const CustomerDetails = ({customer}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false);
  const {
    id,
    firstname,
    lastname,
    middlename = '',
    suffix = '',
    email,
    created_at,
    group_id,
    dob,
    gender,
    extension_attributes: {
      last_login_date,
      is_subscribed
    },
    custom_attributes
  } = customer;

  const practiceName = custom_attributes.find((attr) => attr.attribute_code === 'practice_name')?.value || '';
  const fullName = `${firstname} ${middlename ? `${middlename} ` : ''}${lastname} ${suffix}`;

  const {
    customerGroups,
    customerActiveCardData,
    customerActivePaymentMethod,
  } = useSelector((state: IReduxState) => state.customers);
  const { userStoreData: {storeCode} } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';

  useEffect(() => {
    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }

    getCustomerActiveCreditCardsApi({dispatch, id});
  }, []);

  const customerGroup = customerGroups.length ?
    customerGroups.find((group) => group.id == group_id)?.code
    : '';

  const getCardData = () => {
    let activeCards = [];

    if (customerActivePaymentMethod.code === 'bluepay_payment') {
      activeCards = customerActiveCardData.length
        ? customerActiveCardData.map((item) => item.card)
        : [];
    }

    if (customerActivePaymentMethod.code === 'multi_cc_split') {
      activeCards = customerActiveCardData.length
        ? customerActiveCardData.map((item) => {
          return `xxxxxxxxxxxx${item.last4} - ${item.card_type}[${item.expire_month}/${item.expire_year}] ${item.card_used_on}`;
        })
        : [];
    }

    return activeCards.join('\n') || '---';
  };

  return (
    <>
      <Grid container spacing={5} className="text-dark-gray">
        <Grid item xs={12} sm={6}>
          <Card elevation={20}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2}>
                    <div className="font-bold text-16 capitalize text-black">
                      {fullName}
                    </div>
                  </TableCell>
                </TableRow>
                {isCustomer3 &&
                <TableRow>
                  <TableCell variant="head">Company/Practice Name</TableCell>
                  <TableCell>{practiceName}</TableCell>
                </TableRow>
                }
                <TableRow>
                  <TableCell variant="head">Email</TableCell>
                  <TableCell>{email}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head">Date of Birth</TableCell>
                  <TableCell>
                    {new Date(dob).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head">Gender</TableCell>
                  <TableCell>
                    {genderOptions.find((g) => g.value == gender)?.name}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head">Newsletter Subscription</TableCell>
                  <TableCell>
                    {is_subscribed
                      ? <CheckBoxIcon color="primary" fontSize="small" />
                      : <CheckBoxOutlineBlankIcon fontSize="small" />
                    }
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card elevation={20}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell variant="head">Customer Group</TableCell>
                  <TableCell>{customerGroup}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head">Account Created:</TableCell>
                  <TableCell>
                    {convertUTCDateToLocalDate(created_at).toLocaleString(
                      'en-US',
                      {
                        dateStyle: 'medium',
                        timeStyle: 'medium'
                      }
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head">Last Logged In:</TableCell>
                  <TableCell>
                    {last_login_date === 'Never'
                      ?
                      last_login_date
                      :
                      convertUTCDateToLocalDate(last_login_date).toLocaleString(
                        'en-US',
                        {
                          dateStyle: 'medium',
                          timeStyle: 'medium'
                        }
                      )
                    }
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head" className="align-top">
                    Stored Payment Accounts:
                  </TableCell>
                  <TableCell>
                    {getCardData()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item sm={12} md={6}>
          <Card elevation={20}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <div className="font-bold text-16 capitalize text-black">
                      Other Actions
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <div className="flex items-center flex-wrap">
                      <Button
                        color="primary"
                        variant="contained"
                        className="flex whitespace-no-wrap mr-4 my-4"
                        onClick={() => setOpenDeleteConfirmDialog(true)}
                      >
                        <Icon className="mr-2">delete</Icon>
                        <span>Delete Account</span>
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        className="flex whitespace-no-wrap mr-4 my-4"
                        onClick={() => setOpenResetConfirmDialog(true)}
                      >
                        <Icon className="mr-2">replay</Icon>
                        <span>Send Password Reset Email</span>
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        className="flex whitespace-no-wrap mr-4 my-4"
                        onClick={() => loginAsCustomerApi({dispatch, id})}
                      >
                        <Icon className="mr-2">person</Icon>
                        <span>Login As Customer</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>
      <DesignedConfirmDialog
        open={openDeleteConfirmDialog}
        setOpen={setOpenDeleteConfirmDialog}
        onConfirm={() => {
          const isDeleted = deleteCustomerAccountApi({dispatch, id});

          if (isDeleted) {
            history.push('/customers/list');
          }
        }}
      >
        <div>
          {'Are you sure you want to delete account for '}
          <span className="font-bold">{`${firstname} ${lastname}`}</span>
          {'?'}
        </div>
        <div className="flex items-center justify-center flex-wrap w-full mt-4">
          <Icon color="error" className="mr-4">info</Icon>
          <span className="text-muted">Once you delete account, data will be lost forever</span>
        </div>
      </DesignedConfirmDialog>
      <DesignedConfirmDialog
        open={openResetConfirmDialog}
        setOpen={setOpenResetConfirmDialog}
        onConfirm={() => resetCustomerPasswordApi({dispatch, email})}
      >
        {'Are you sure you want reset customer password?'}
      </DesignedConfirmDialog>
    </>
  );
};

export default CustomerDetails;
